import React, { useContext, useEffect, useState } from 'react';
import Title from '../../components/Title';
import Label from '../../components/Label';
import SubmitButton from '../../components/SubmitButton';
import SmallButton from '../../components/SmallButton';
import { axiosKK } from '../../services/networkRequest';
import {
  Discount,
  DiscountAndCount,
  DiscountListResponse,
} from '../../models/APIModels';
import { RouteProps, useHistory } from 'react-router-dom';
import Moment from 'moment';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import { DiscountModal } from '../../components/admin/DiscountModal';
import UserContext from '../../contexts/UserContext';
import { Role } from '../../utilities/Role';
import { useDiscountFilters } from './useDiscountFilters';
import Switch from 'react-switch';

export default function DiscountComponent(this: any): JSX.Element {
  const history = useHistory();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<boolean | number>(false);
  const [discounts, setDiscounts] = useState<DiscountAndCount[]>([]);
  const [reloadData, setReloadData] = useState(false);
  const { user: loggedInUser } = useContext(UserContext);

  const {
    filterStatus,
    setFilterStatus,
    filterUpFront,
    setFilterUpFront,
    filterRecurring,
    setFilterRecurring,
    filteredDiscounts,
  } = useDiscountFilters(discounts);

  useEffect(() => {
    window.scrollTo(0, 0);

    axiosKK.get<DiscountListResponse>('/discounts').then(({ data }) => {
      setDiscounts(data.discounts.filter((d) => !d.deletedAt));
    });
  }, [reloadData]);

  return (
    <>
      <section className="nav-status-bar">
        <div className="status-bar-content">
          <i className="fas fa-arrow-circle-left" />
          <SmallButton
            onClick={() => {
              history.push('/admin/dashboard');
            }}
          >
            Back
          </SmallButton>
        </div>
      </section>

      <Title>Discounts</Title>
      <section className="admin-all-discounts-info">
        <div className="discount-controls-container">
          <div className="filters-group">
            <div className="filter-item">
              <label>Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            
            <div className="filter-item toggle-item">
              <label>Up-Front</label>
              <Switch 
                checked={filterUpFront} 
                onChange={setFilterUpFront}
                onColor="#6c3cd9"
                offColor="#dbdbdb"
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={44}
                handleDiameter={18}
              />
            </div>
            
            <div className="filter-item toggle-item">
              <label>Recurring</label>
              <Switch 
                checked={filterRecurring} 
                onChange={setFilterRecurring}
                onColor="#6c3cd9"
                offColor="#dbdbdb"
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={44}
                handleDiameter={18}
              />
            </div>
          </div>

          {loggedInUser?.role === Role.Admin && (
            <div className="action-group">
              <SubmitButton
                onClick={() => {
                  setShowCreateModal(true);
                }}
              >
                <i className="fas fa-plus" /> Create New
              </SubmitButton>
            </div>
          )}
        </div>

        <div className="table">
          <div className="thead">
            <div className="tr">
              <div className="td xs">ID</div>
              <div className="td large">Code</div>
              <div className="td small">Status</div>
              <div className="td small">Discount</div>
              <div className="td small">Up-Front</div>
              <div className="td small">Recurring</div>
              <div className="td small">No. of Uses</div>
              <div className="td mid">Expiry</div>
              <div className="td mid">Actions</div>
            </div>
          </div>
          <div className="tbody">
            {filteredDiscounts.map((each) => (
              <Row
                id={each.id}
                key={each.id}
                discount={each}
                setShowEdit={(id) => setShowEditModal(id)}
                reloadData={() => setReloadData(!reloadData)}
                count={each}
              />
            ))}
          </div>
        </div>
      </section>

      {loggedInUser?.role === Role.Admin && (
        <DiscountModal
          showCreate={showCreateModal}
          showEdit={showEditModal}
          close={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
          }}
          discount={discounts.find((value) => value.id === showEditModal)}
          refreshDiscounts={() => setReloadData(!reloadData)}
          updateDiscounts={(discount) => {
            setDiscounts([
              discount,
              ...discounts.filter((value) => value.id !== discount.id),
            ]);
          }}
        />
      )}
    </>
  );
}

interface RowProps extends RouteProps {
  reloadData: () => void;
  setShowEdit: (id: number) => void;
  discount: Discount;
  count: DiscountAndCount;
  id: number;
}

function Row({
  discount,
  count,
  setShowEdit,
  reloadData,
}: RowProps): JSX.Element {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { user: loggedInUser } = useContext(UserContext);

  const currentDate = Moment();
  const expireDateIsBeforeCurrent = Moment(discount.expires).isBefore(
    currentDate,
  );

  return (
    <>
      <div className="tr">
        <Label>ID</Label>
        <div className="td xs">{discount.id}</div>
        <Label>Code</Label>
        <div className="td large code">{discount.discountCode}</div>
        <Label>Status</Label>
        <div className="td small">
          {discount.expires && expireDateIsBeforeCurrent ? (
            <span className="inactive">inactive</span>
          ) : (
            <span className="active">active</span>
          )}
        </div>
        <Label>Percentage</Label>
        <div className="td small">{discount.discountPercentage}%</div>
        <Label>Up-Front</Label>
        <div className="td small">{discount.upFront ? 'Yes' : 'No'}</div>
        <Label>Recurring</Label>
        <div className="td small">{discount.recurring ? 'Yes' : 'No'}</div>
        <Label>Number of Uses</Label>
        <div className="td small">{count.count}</div>
        <Label>Expiry</Label>
        <div className="td mid">
          {discount.expires ? (
            <>{Moment(discount.expires).format('MMM Do YYYY')}</>
          ) : (
            <>N/A</>
          )}
        </div>
        <Label>Actions</Label>
        <div className="td mid discount-button-wrapper">
          {loggedInUser?.role === Role.Admin && (
            <SubmitButton
              onClick={() => setShowEdit(discount.id)}
            >
              <i className="fas fa-edit" /> Edit
            </SubmitButton>
          )}
          {discount.expires && expireDateIsBeforeCurrent ? (
            <SubmitButton
              className="activate"
              onClick={() => setShowActivateModal(true)}
            >
              <i className="fas fa-trash-restore" /> Reactivate
            </SubmitButton>
          ) : (
            <SubmitButton
              className="deactivate-btn"
              onClick={() => setShowConfirmModal(true)}
            >
              <i className="fas fa-ban" /> Deactivate
            </SubmitButton>
          )}
          {loggedInUser?.role === Role.Admin && (
            <SubmitButton
              red={true}
              className="delete-btn"
              onClick={() => setShowDeleteModal(true)}
            >
              <i className="fas fa-trash" /> Delete
            </SubmitButton>
          )}
        </div>
      </div>

      <ConfirmationModal
        id={discount.id}
        show={showConfirmModal}
        close={() => setShowConfirmModal(false)}
        success={() => {
          setShowConfirmModal(false);
          reloadData();
        }}
        failure={() => setShowConfirmModal(true)}
        directory="/discounts/deactivate/"
        warningMessage="Are you sure you want to deactivate this discount code?"
      />

      <ConfirmationModal
        id={discount.id}
        show={showActivateModal}
        close={() => setShowActivateModal(false)}
        success={() => {
          setShowActivateModal(false);
          setShowEdit(discount.id);
          reloadData();
        }}
        failure={() => setShowActivateModal(true)}
        directory="/discounts/reactivate/"
        warningMessage="Do you want to reactivate this discount code?"
      />

      <DeleteConfirmationModal
        show={showDeleteModal}
        close={() => setShowDeleteModal(false)}
        success={() => {
          setShowDeleteModal(false);
          reloadData();
        }}
        failure={() => setShowDeleteModal(true)}
        directory="/discounts/"
        id={discount.id}
        warningMessage="Are you sure you want to permanently delete this discount code? This cannot be undone."
      />
    </>
  );
}
